import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, get, errMsg } from "@/lib/api";

export function UseForJobDialog({ template, jobId, onClose, onDone }) {
  const qc = useQueryClient();
  const [job, setJob] = useState(jobId || "");
  const [copy, setCopy] = useState(false);
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({ queryKey: ["jobs-min"], queryFn: () => get("/jobs"), enabled: !!template && !jobId });
  const jobs = (Array.isArray(data) ? data : data?.items || data?.jobs || []).filter((j) => j.status !== "closed");
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${job}/assessment-template`, { template_id: template.id, copy_for_job: copy });
      toast.success(`"${template.name}" is now this job's assessment`);
      qc.invalidateQueries({ queryKey: ["job-assessment", job] }); onDone?.(); onClose();
    } catch (e) {
      const w = e?.response?.data?.detail?.warnings;
      toast.error(w ? `${errMsg(e)}: ${w.join("; ")}` : errMsg(e));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!template} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="use-for-job-dialog">
        <DialogHeader>
          <DialogTitle>Use "{template?.name}" for a job</DialogTitle>
          <DialogDescription>Candidates you invite from now on take this assessment. People already invited keep the version they were sent.</DialogDescription>
        </DialogHeader>
        {!jobId && (
          <label className="space-y-1 text-sm"><span className="text-ink-secondary">Job</span>
            <select value={job} onChange={(e) => setJob(e.target.value)} className="block h-9 w-full rounded-lg border border-line bg-white px-2" data-testid="use-for-job-select">
              <option value="">Choose a job…</option>
              {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
            </select>
            {data && !jobs.length && <span className="text-xs text-caution" data-testid="use-for-job-no-jobs">No open jobs yet. Create a job first.</span>}</label>
        )}
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={copy} onChange={(e) => setCopy(e.target.checked)} className="mt-1" data-testid="use-for-job-copy" />
          <span>Make a separate copy for this job<span className="block text-xs text-ink-muted">Edits to the copy won't change the library version or other jobs.</span></span>
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!job || busy} data-testid="use-for-job-confirm">Use this assessment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
